import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import LandingPage from "@/pages/LandingPage";
import { Terms, Privacy, RefundPolicy, Contact } from "@/pages/Legal";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import UploadResume from "@/pages/UploadResume";
import JobSearch from "@/pages/JobSearch";
import JobMatches from "@/pages/JobMatches";
import Applications from "@/pages/Applications";
import ATSOptimizer from "@/pages/ATSOptimizer";
import CoverLetter from "@/pages/CoverLetter";
import InterviewPrep from "@/pages/InterviewPrep";
import AutoApply from "@/pages/AutoApply";
import Connections from "@/pages/Connections";
import Calendar from "@/pages/Calendar";
import Profile from "@/pages/Profile";
import Subscribe from "@/pages/Subscribe";
import TailoredResume from "@/pages/TailoredResume";
import SalaryInsights from "@/pages/SalaryInsights";
import Placeholder from "@/pages/Placeholder";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-violet-300 jr-pulse">Loading…</div>;
  if (!user && !location.state?.user) return <Navigate to="/login" replace />;
  return children;
}

function PublicHome() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-violet-300 jr-pulse">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

function AppRouter() {
  const location = useLocation();
  // Detect session_id synchronously to handle OAuth callback before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/upload-resume" element={<UploadResume />} />
        <Route path="/job-search" element={<JobSearch />} />
        <Route path="/job-matches" element={<JobMatches />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/ats-optimizer" element={<ATSOptimizer />} />
        <Route path="/cover-letter" element={<CoverLetter />} />
        <Route path="/interview-prep" element={<InterviewPrep />} />
        <Route path="/auto-apply" element={<AutoApply />} />
        <Route path="/connections" element={<Connections />} />
        <Route path="/salary-insights" element={<SalaryInsights />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/tailored-resume" element={<TailoredResume />} />
        <Route path="/agents/browser" element={<Placeholder title="Browser Agent" description="Apify-powered browser agent that scrapes LinkedIn, Indeed, Glassdoor and submits application forms with your approval." />} />
        <Route path="/agents/cv-parser" element={<Placeholder title="CV Parser Agent" description="Specialized Claude Sonnet 4.5 agent that extracts structured data from your resume. Used automatically when you upload." />} />
        <Route path="/agents/jd-analyzer" element={<Placeholder title="JD Analyzer Agent" description="Decomposes job descriptions into required skills, responsibilities, seniority and keywords." />} />
        <Route path="/agents/job-match" element={<Placeholder title="Job Match Agent" description="Deep multi-factor matching between your profile and any job. Try 'Deep Analyze' on Job Matches page." />} />
        <Route path="/agents/resume-tailor" element={<Placeholder title="Resume Tailor Agent" description="Rewrites your resume bullet-by-bullet for a target job. Try it from the Job Matches page." />} />
        <Route path="/agents/tracker" element={<Placeholder title="Tracker Agent" description="Logs every action across your job hunt. View activity on the Dashboard." />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
